/* Haptic feedback module.
   Primary: Telegram.WebApp.HapticFeedback. Fallback: navigator.vibrate.
   iOS Safari outside TG has no vibrate — fire() is silent-safe.
   State persists in localStorage under 'haptics_enabled'. */

const PLATFORM = (() => {
  if (typeof window === 'undefined') return 'none';
  const tg = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.HapticFeedback;
  if (tg && typeof tg.impactOccurred === 'function') return 'tg';
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') return 'vibrate';
  return 'none';
})();

const VIB_MS = { light: 10, soft: 15, medium: 25, rigid: 35, heavy: 50 };
const VIB_NOTIFY = { success: [20, 40, 30], warning: [30, 60, 40], error: [50, 80, 60] };
const VIB_SELECTION = 8;

const STORE_KEY = 'haptics_enabled';
let enabled = (() => {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(STORE_KEY) : null;
    return v === null ? true : v === '1';
  } catch { return true; }
})();

const _timers = new Set();

export function setHapticsEnabled(v) {
  enabled = !!v;
  try { localStorage.setItem(STORE_KEY, enabled ? '1' : '0'); } catch {}
  if (!enabled) cancelHaptics();
}
export function isHapticsEnabled() { return enabled; }
export function isHapticsSupported() { return PLATFORM !== 'none'; }

function fire(step) {
  if (!enabled || PLATFORM === 'none') return;
  try {
    if (PLATFORM === 'tg') {
      const h = window.Telegram.WebApp.HapticFeedback;
      if (step.kind === 'impact') h.impactOccurred(step.style);
      else if (step.kind === 'notify') h.notificationOccurred(step.style);
      else h.selectionChanged();
      return;
    }
    if (PLATFORM === 'vibrate') {
      if (step.kind === 'impact') navigator.vibrate(VIB_MS[step.style] ?? 15);
      else if (step.kind === 'notify') navigator.vibrate(VIB_NOTIFY[step.style] ?? 30);
      else navigator.vibrate(VIB_SELECTION);
    }
  } catch {}
}

export const H = {
  selection: () => fire({ kind: 'selection' }),
  light:     () => fire({ kind: 'impact', style: 'light' }),
  soft:      () => fire({ kind: 'impact', style: 'soft' }),
  medium:    () => fire({ kind: 'impact', style: 'medium' }),
  rigid:     () => fire({ kind: 'impact', style: 'rigid' }),
  heavy:     () => fire({ kind: 'impact', style: 'heavy' }),
  success:   () => fire({ kind: 'notify', style: 'success' }),
  warning:   () => fire({ kind: 'notify', style: 'warning' }),
  error:     () => fire({ kind: 'notify', style: 'error' }),
};

function later(ms, fn) {
  const id = setTimeout(() => { _timers.delete(id); fn(); }, ms);
  _timers.add(id);
  return id;
}

export function cancelHaptics() {
  _timers.forEach(id => clearTimeout(id));
  _timers.clear();
  if (PLATFORM === 'vibrate') { try { navigator.vibrate(0); } catch {} }
}

/* Named patterns for case-opening phases.
   Rarity: 'common' | 'epic' | 'legendary' | 'chroma' (chroma == хромо). */
export const CasePatterns = {
  press() { H.selection(); later(80, H.light); },
  land()  { H.light(); },
  clipMorph() {
    H.rigid();            later(60,  H.soft);
    later(120, H.rigid);  later(180, H.soft);
    later(240, H.rigid);  later(300, H.soft);
  },
  clipIn(rarity) {
    if (rarity === 'epic')          { H.medium(); }
    else if (rarity === 'legendary'){ H.rigid(); later(60, H.soft); }
    else if (rarity === 'chroma')   {
      H.rigid();            later(60,  H.soft);
      later(120, H.rigid);  later(180, H.soft);
      later(240, H.rigid);  later(300, H.soft);
    }
    else                            { H.light(); }
  },
  prelude(rarity) {
    this.press();
    later(620, () => this.clipIn(rarity));
  },

  pin(rarity, i, total) {
    if (rarity === 'chroma')                  { H.light(); later(70, H.soft); }
    else if (rarity === 'legendary' && i === total - 2) { H.rigid(); }
    else                                      { i % 2 === 0 ? H.selection() : H.light(); }
  },

  yield_(rarity) {
    if (rarity === 'common') H.medium();
    else                     H.heavy();
    later(220, H.soft);
    if (rarity !== 'common') later(290, H.soft);
  },

  drop(rarity) {
    H.success(); H.heavy();
    if (rarity === 'legendary') {
      later(100, H.heavy);
      later(300, H.light); later(520, H.light);
    } else if (rarity === 'chroma') {
      later(90,  H.heavy); later(170, H.heavy);
      later(330, H.rigid); later(390, H.soft);
      later(450, H.rigid); later(530, H.soft);
    } else if (rarity === 'epic') {
      later(80,  H.heavy);
      later(220, H.soft);
    }
  },

  breakSnap() { H.warning(); },
  breakFinal(){ later(100, H.error); },

  skip(rarity) {
    cancelHaptics();
    if (rarity === 'common')    { H.success(); return; }
    if (rarity === 'epic')      { H.heavy(); later(120, H.success); return; }
    if (rarity === 'legendary') { H.heavy(); later(100, H.heavy); later(250, H.success); later(400, H.light); return; }
    H.rigid(); later(60, H.soft); later(120, H.rigid);
    later(270, H.success); later(310, H.heavy);
    later(410, H.heavy);   later(510, H.heavy);
  },
};
